/**
 * AuthContext - Authentication State Management
 *
 * Provides authentication state and functions throughout the app.
 * Handles login, logout, token storage, and user session management.
 */

import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import apiClient from '../services/api';

const AuthContext = createContext(null);

// Token storage keys
const TOKEN_KEY = 'pulse_auth_token';
const USER_KEY = 'pulse_user';

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  /**
   * Initialize auth state from localStorage on mount
   */
  useEffect(() => {
    const initAuth = () => {
      try {
        const storedToken = localStorage.getItem(TOKEN_KEY);
        const storedUser = localStorage.getItem(USER_KEY);

        if (storedToken && storedUser) {
          setToken(storedToken);
          setUser(JSON.parse(storedUser));
          // Set default authorization header
          apiClient.defaults.headers.common['Authorization'] = `Bearer ${storedToken}`;
        }
      } catch (err) {
        console.error('Error initializing auth:', err);
        // Clear invalid data
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(USER_KEY);
      } finally {
        setLoading(false);
      }
    };

    initAuth();
  }, []);

  /**
   * Login with username and password
   */
  const login = useCallback(async (username, password) => {
    setLoading(true);
    setError(null);

    try {
      const response = await apiClient.post('/api/auth/login', {
        username,
        password,
      });

      const { token: authToken, user: userData } = response.data;

      // Store token and user
      localStorage.setItem(TOKEN_KEY, authToken);
      localStorage.setItem(USER_KEY, JSON.stringify(userData));

      // Update state
      setToken(authToken);
      setUser(userData);

      // Set default authorization header for future requests
      apiClient.defaults.headers.common['Authorization'] = `Bearer ${authToken}`;

      return { success: true };
    } catch (err) {
      const errorMessage = err.response?.data?.detail || 'Login failed. Please try again.';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Logout and clear session
   */
  const logout = useCallback(async (reason = null) => {
    setLoading(true);

    try {
      // Call backend logout endpoint (optional - JWT is stateless)
      if (token) {
        await apiClient.post('/api/auth/logout');
      }
    } catch (err) {
      console.error('Logout error:', err);
      // Continue with client-side logout even if backend call fails
    } finally {
      // Clear storage
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(USER_KEY);

      // Clear state
      setToken(null);
      setUser(null);
      setError(reason);

      // Remove authorization header
      delete apiClient.defaults.headers.common['Authorization'];

      setLoading(false);
    }
  }, [token]);

  /**
   * Login with Snowflake credentials
   * Role, database, and schema will be determined from user's Snowflake defaults
   */
  const snowflakeLogin = useCallback(async (username, password, tenantId, passcode = null) => {
    setLoading(true);
    setError(null);

    try {
      const response = await apiClient.post('/api/auth/snowflake-login', {
        username,
        password,
        tenant_id: tenantId,
        passcode,
      });

      const { token: authToken, user: userData, tenant_id } = response.data;

      // Store token and user
      localStorage.setItem(TOKEN_KEY, authToken);
      localStorage.setItem(USER_KEY, JSON.stringify(userData));
      if (tenant_id) {
        localStorage.setItem('pulse_tenant_id', tenant_id);
      }

      // Update state
      setToken(authToken);
      setUser(userData);

      // Set default authorization header for future requests
      apiClient.defaults.headers.common['Authorization'] = `Bearer ${authToken}`;

      return { success: true };
    } catch (err) {
      const errorMessage = err.response?.data?.detail || 'Snowflake login failed. Please check your credentials.';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Login with local credentials (tenant selection)
   */
  const localLogin = useCallback((authToken, userData, tenantId) => {
    // Store token and user
    localStorage.setItem(TOKEN_KEY, authToken);
    localStorage.setItem(USER_KEY, JSON.stringify(userData));
    if (tenantId) {
      localStorage.setItem('pulse_tenant_id', tenantId);
    }

    // Update state
    setToken(authToken);
    setUser(userData);

    // Set default authorization header for future requests
    apiClient.defaults.headers.common['Authorization'] = `Bearer ${authToken}`;
  }, []);

  /**
   * Refresh user data from backend
   */
  const refreshUser = useCallback(async () => {
    if (!token) return;

    try {
      const response = await apiClient.get('/api/auth/me');
      const userData = response.data.user;

      setUser(userData);
      localStorage.setItem(USER_KEY, JSON.stringify(userData));
    } catch (err) {
      console.error('Error refreshing user:', err);
      // If token is invalid, logout
      if (err.response?.status === 401) {
        await logout('Session expired. Please login again.');
      }
    }
  }, [token, logout]);

  /**
   * Check if user is authenticated
   */
  const isAuthenticated = useCallback(() => {
    return !!token && !!user;
  }, [token, user]);

  const value = {
    user,
    token,
    loading,
    error,
    login,
    snowflakeLogin,
    logout,
    localLogin,
    refreshUser,
    isAuthenticated,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

AuthProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

/**
 * Hook to use auth context
 */
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
